(() => {
  const TAG_NAME = 'vl-collection-compare';
  const PRODUCT_INPUT = '[data-vl-collection-compare-input]';
  const FILTER_COLLECTION_LINK = '[data-vl-collection-compare-filter-link]';
  const SESSION_STORAGE_KEY = 'vl_collection_compare_v1';
  const STORAGE_VERSION = 1;
  const MAX_PRODUCTS = 3;
  const MIN_PRODUCTS = 2;

  if (customElements.get(TAG_NAME)) return;

  class VLCollectionCompare extends HTMLElement {
    constructor() {
      super();
      this.selected = [];
      this.abortController = null;
      this.observer = null;
      this.isExpanded = false;
      this.isDesktopExpanded = false;
      this.mobileMode = null;
      this.pageScrollLock = null;
      this.handleChange = this.handleChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.mobileMode = this.isMobile();
      this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
      document.addEventListener('click', this.handleDocumentClick, { signal: this.abortController.signal });
      document.addEventListener('change', this.handleChange, { signal: this.abortController.signal });
      document.addEventListener('keydown', this.handleKeydown, { signal: this.abortController.signal });
      window.addEventListener('pageshow', this.handlePageShow, { signal: this.abortController.signal });
      window.addEventListener('resize', this.handleResize, { signal: this.abortController.signal });
      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.restoreSelection();
      if (!this.isMobile()) this.isDesktopExpanded = this.selected.length >= MIN_PRODUCTS;
      this.syncInputs();
      this.render();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();
      this.observer = null;
      this.unlockPageScroll();
    }

    productFromInput(input) {
      return {
        id: String(input.dataset.productId || ''),
        handle: String(input.dataset.productHandle || ''),
        name: String(input.dataset.productName || ''),
        image: String(input.dataset.productImage || ''),
        price: String(input.dataset.productPrice || ''),
        comparePrice: String(input.dataset.productComparePrice || '')
      };
    }

    handleChange(event) {
      const input = event.target.closest(PRODUCT_INPUT);
      if (!input) return;

      const product = this.productFromInput(input);
      if (!product.id || !product.handle) return;
      let action = null;

      if (input.checked) {
        if (this.selected.length >= MAX_PRODUCTS) {
          input.checked = false;
          this.announce(this.dataset.limitMessage || 'You can compare up to three products.');
          return;
        }
        if (!this.selected.some((item) => item.id === product.id)) {
          this.selected.push(product);
          action = 'add';
        }
      } else {
        if (this.selected.some((item) => item.id === product.id)) action = 'remove';
        this.selected = this.selected.filter((item) => item.id !== product.id);
      }

      if (!action) return;

      this.persistSelection();
      if (!this.isMobile()) {
        this.isDesktopExpanded = this.selected.length >= MIN_PRODUCTS;
      }
      this.syncInputs();
      this.render();
      this.trackAnalytics('compare_product_select', this.selected, {
        operatedProductHandle: product.handle,
        action
      });
      this.announce(
        String(this.dataset.countMessage || '{count} products selected for comparison.')
          .replace('{count}', this.selected.length)
      );
    }

    handleClick(event) {
      const toggle = event.target.closest('[data-vl-collection-compare-toggle]');
      if (toggle) {
        if (this.isMobile()) this.isExpanded = !this.isExpanded;
        else this.isDesktopExpanded = !this.isDesktopExpanded;
        this.render();
        return;
      }

      if (event.target.closest('[data-vl-collection-compare-backdrop]')) {
        this.isExpanded = false;
        this.render();
        this.querySelector('[data-vl-collection-compare-toggle]')?.focus();
        return;
      }

      const remove = event.target.closest('[data-vl-collection-compare-remove]');
      if (remove) {
        this.removeProduct(remove.dataset.productId);
        return;
      }

      if (event.target.closest('[data-vl-collection-compare-clear]')) {
        this.clear();
        return;
      }

      const submit = event.target.closest('[data-vl-collection-compare-submit]');
      if (submit) this.submit(event, submit);
    }

    handleDocumentClick(event) {
      const link = event.target.closest(FILTER_COLLECTION_LINK);
      if (!link || !this.isPlainNavigationClick(event, link) || !this.selected.length) return;

      const targetPath = this.normalizedPath(link.href);
      const currentPath = this.normalizedPath(window.location.href);
      if (!targetPath || targetPath === currentPath) return;
      this.persistSelection(targetPath, currentPath, this.filterNavigationToken(link.href));
    }

    handleMutations(mutations) {
      if (mutations.some((mutation) => mutation.addedNodes.length)) this.syncInputs();
    }

    handlePageShow(event) {
      if (!event.persisted) return;
      this.selected = [];
      this.isExpanded = false;
      this.isDesktopExpanded = false;
      this.clearStoredSelection();
      this.syncInputs();
      this.render();
    }

    handleKeydown(event) {
      if (!this.isMobile() || !this.isExpanded) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.isExpanded = false;
        this.render();
        this.querySelector('[data-vl-collection-compare-toggle]')?.focus();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.querySelectorAll('button:not([disabled]):not([hidden])'))
        .filter((element) => element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleResize() {
      const mobile = this.isMobile();
      if (mobile === this.mobileMode) return;
      this.mobileMode = mobile;
      if (mobile) this.isExpanded = false;
      else this.isDesktopExpanded = this.selected.length >= MIN_PRODUCTS;
      this.render();
    }

    removeProduct(productId) {
      const product = this.selected.find((item) => item.id === String(productId));
      if (!product) return;
      this.selected = this.selected.filter((item) => item.id !== String(productId));
      if (!this.selected.length) this.isExpanded = false;
      if (!this.isMobile()) this.isDesktopExpanded = this.selected.length >= MIN_PRODUCTS;
      this.persistSelection();
      this.syncInputs();
      this.render();
      this.trackAnalytics('compare_product_select', this.selected, {
        operatedProductHandle: product.handle,
        action: 'remove'
      });
    }

    clear() {
      const clearedProducts = [...this.selected];
      if (!clearedProducts.length) return;
      this.selected = [];
      this.isExpanded = false;
      this.isDesktopExpanded = false;
      this.clearStoredSelection();
      this.syncInputs();
      this.render();
      this.trackAnalytics('compare_clear', clearedProducts, { action: 'clear' });
      this.announce(this.dataset.clearMessage || 'Compare selection cleared.');
    }

    submit(event, submit) {
      if (this.selected.length < MIN_PRODUCTS) {
        event.preventDefault();
        return;
      }

      const storeClass = customElements.get('vl-product-compare-manager');
      try {
        storeClass?.writeStoredHistory(this.selected);
      } catch (error) {
        this.announce(this.dataset.storageErrorMessage || 'Your comparison will open, but history could not be saved.');
      }

      const url = storeClass?.compareUrl(this.dataset.comparePath, this.selected)
        || `${this.dataset.comparePath}?products=${this.selected.map((item) => item.handle).join(',')}`;
      this.trackAnalytics('compare_start', this.selected, { action: 'start' });
      window.location.assign(url);
    }

    syncInputs() {
      const selectedIds = new Set(this.selected.map((item) => item.id));
      document.querySelectorAll(PRODUCT_INPUT).forEach((input) => {
        const isSelected = selectedIds.has(String(input.dataset.productId));
        input.checked = isSelected;
        const atLimit = !isSelected && this.selected.length >= MAX_PRODUCTS;
        input.disabled = atLimit;
        input.setAttribute('aria-disabled', String(atLimit));
        input.closest('label')?.classList.toggle('is-disabled', atLimit);
      });
    }

    render() {
      const count = this.querySelector('[data-vl-collection-compare-count]');
      const panel = this.querySelector('[data-vl-collection-compare-panel]');
      const toggle = this.querySelector('[data-vl-collection-compare-toggle]');
      const list = this.querySelector('[data-vl-collection-compare-list]');
      const backdrop = this.querySelector('[data-vl-collection-compare-backdrop]');
      const submits = this.querySelectorAll('[data-vl-collection-compare-submit]');
      const hasProducts = this.selected.length > 0;

      this.hidden = !hasProducts;
      if (!hasProducts) {
        this.unlockPageScroll();
        return;
      }
      if (count) count.textContent = String(this.selected.length);
      this.classList.toggle('is-ready', this.selected.length >= MIN_PRODUCTS);

      const mobile = this.isMobile();
      const expanded = mobile ? this.isExpanded : this.isDesktopExpanded;
      this.classList.toggle('is-expanded', expanded);
      if (toggle) {
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.setAttribute(
          'aria-label',
          expanded
            ? (this.dataset.collapseMessage || 'Collapse product compare')
            : (this.dataset.expandMessage || 'Expand product compare')
        );
      }
      if (panel) panel.hidden = !expanded;
      if (backdrop) backdrop.hidden = !(mobile && expanded);
      if (mobile && expanded) this.lockPageScroll();
      else this.unlockPageScroll();
      if (list) {
        const slots = [...this.selected];
        while (slots.length < MAX_PRODUCTS) slots.push(null);
        list.innerHTML = slots.map((product, index) => product ? `
          <li class="vl-collection-compare__item">
            <div class="vl-collection-compare__image">
              ${product.image ? `<img src="${this.escape(product.image)}" alt="" loading="lazy">` : ''}
            </div>
            <div class="vl-collection-compare__product-info">
              <span class="vl-collection-compare__product-name">${this.escape(product.name)}</span>
              ${product.price ? `
                <span class="vl-collection-compare__prices">
                  <span class="vl-collection-compare__price">${this.escape(product.price)}</span>
                  ${product.comparePrice ? `<s class="vl-collection-compare__compare-price">${this.escape(product.comparePrice)}</s>` : ''}
                </span>
              ` : ''}
            </div>
            <button
              type="button"
              class="vl-collection-compare__remove"
              data-vl-collection-compare-remove
              data-product-id="${this.escape(product.id)}"
              aria-label="${this.escape(String(this.dataset.removeMessage || 'Remove {name} from compare selection').replace('{name}', product.name))}"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path d="m7 7 10 10M17 7 7 17" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
              </svg>
            </button>
          </li>
        ` : `
          <li class="vl-collection-compare__item vl-collection-compare__item--empty" aria-hidden="true">
            <span>${index + 1}</span>
          </li>
        `).join('');
      }

      const enabled = this.selected.length >= MIN_PRODUCTS;
      submits.forEach((submit) => {
        submit.disabled = !enabled;
        submit.classList.toggle('is-disabled', !enabled);
      });
    }

    isMobile() {
      return window.matchMedia('(max-width: 1023px)').matches;
    }

    restoreSelection() {
      const stored = this.readStoredSelection();
      if (!stored) return;

      const currentPath = this.normalizedPath(window.location.href);
      const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
      const navigationType = navigationEntry?.type || '';
      const referrerPath = document.referrer ? this.normalizedPath(document.referrer) : '';
      const currentFilterToken = this.filterNavigationToken(window.location.href);
      const isCurrentCollectionReload = stored.collectionPath === currentPath
        && (navigationType === 'reload' || (!navigationType && referrerPath === currentPath));
      const isExactFilterTarget = stored.filterTargetPath === currentPath;
      const isRedirectedFilterTarget = Boolean(
        stored.filterTargetPath
        && referrerPath === stored.collectionPath
        && currentFilterToken
        && (!stored.filterNavigationToken || stored.filterNavigationToken === currentFilterToken)
      );
      const isFilterTransfer = isExactFilterTarget || isRedirectedFilterTarget;

      if (!isCurrentCollectionReload && !isFilterTransfer) {
        this.clearStoredSelection();
        return;
      }

      this.selected = stored.products;
      this.isExpanded = false;
      this.persistSelection(null, currentPath, null);
    }

    readStoredSelection() {
      try {
        const parsed = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || 'null');
        if (!parsed || parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.products)) {
          this.clearStoredSelection();
          return null;
        }

        const products = [];
        const ids = new Set();
        parsed.products.slice(0, MAX_PRODUCTS).forEach((product) => {
          const normalized = this.normalizedStoredProduct(product);
          if (!normalized || ids.has(normalized.id)) return;
          ids.add(normalized.id);
          products.push(normalized);
        });
        if (!products.length) {
          this.clearStoredSelection();
          return null;
        }

        return {
          products,
          collectionPath: this.normalizedPath(parsed.collectionPath),
          filterTargetPath: this.normalizedPath(parsed.filterTargetPath),
          filterNavigationToken: this.normalizedFilterNavigationToken(parsed.filterNavigationToken)
        };
      } catch (error) {
        this.clearStoredSelection();
        return null;
      }
    }

    persistSelection(
      filterTargetPath = null,
      collectionPath = this.normalizedPath(window.location.href),
      filterNavigationToken = null
    ) {
      if (!this.selected.length) {
        this.clearStoredSelection();
        return;
      }

      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
          version: STORAGE_VERSION,
          collectionPath,
          filterTargetPath,
          filterNavigationToken,
          products: this.selected.slice(0, MAX_PRODUCTS).map((product) => ({
            id: String(product.id || ''),
            handle: String(product.handle || ''),
            name: String(product.name || ''),
            image: String(product.image || ''),
            price: String(product.price || ''),
            comparePrice: String(product.comparePrice || '')
          }))
        }));
      } catch (error) {
        // Session persistence is an enhancement and must not interrupt comparison controls.
      }
    }

    clearStoredSelection() {
      try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch (error) {
        // Storage may be unavailable in privacy-restricted browsing contexts.
      }
    }

    normalizedStoredProduct(product) {
      if (!product || typeof product !== 'object') return null;
      const id = String(product.id || '');
      const handle = String(product.handle || '');
      if (!id || !handle) return null;
      return {
        id,
        handle,
        name: String(product.name || ''),
        image: String(product.image || ''),
        price: String(product.price || ''),
        comparePrice: String(product.comparePrice || '')
      };
    }

    normalizedPath(value) {
      if (!value) return '';
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return '';
        const pathname = url.pathname.replace(/\/+$/, '');
        return pathname || '/';
      } catch (error) {
        return '';
      }
    }

    filterNavigationToken(value) {
      if (!value) return '';
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return '';
        return this.normalizedFilterNavigationToken(url.searchParams.get('page_source'));
      } catch (error) {
        return '';
      }
    }

    normalizedFilterNavigationToken(value) {
      const token = String(value || '');
      return /^faq_Filters(?:Desktop|Mobile)\|/.test(token) ? token : '';
    }

    isPlainNavigationClick(event, link) {
      return event.button === 0
        && !event.defaultPrevented
        && !event.metaKey
        && !event.ctrlKey
        && !event.shiftKey
        && !event.altKey
        && !link.hasAttribute('download')
        && (!link.target || link.target === '_self');
    }

    trackAnalytics(eventName, products, options = {}) {
      const storeClass = customElements.get('vl-product-compare-manager');
      if (typeof storeClass?.pushAnalytics === 'function') {
        storeClass.pushAnalytics(eventName, {
          context: 'collection',
          products,
          ...options
        });
        return;
      }

      try {
        const handles = (Array.isArray(products) ? products : [])
          .slice(0, MAX_PRODUCTS)
          .map((product) => product?.handle || null);
        while (handles.length < MAX_PRODUCTS) handles.push(null);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'ga4Event',
          event_name: eventName,
          event_parameters: {
            compare_context: 'collection',
            product_count: handles.filter(Boolean).length,
            product_1_handle: handles[0],
            product_2_handle: handles[1],
            product_3_handle: handles[2],
            operated_product_handle: options.operatedProductHandle || null,
            previous_product_handle: null,
            operated_slot: null,
            initial_source: null,
            action: options.action || null
          }
        });
      } catch (error) {
        // Analytics must never interrupt the collection comparison controls.
      }
    }

    lockPageScroll() {
      if (this.pageScrollLock) return;
      const root = document.documentElement;
      const body = document.body;
      const scrollY = window.scrollY;
      this.pageScrollLock = {
        scrollY,
        rootOverflow: root.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width
      };
      root.classList.add('vl-collection-compare-open');
      body.classList.add('vl-collection-compare-open');
      root.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `${-scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }

    unlockPageScroll() {
      const lock = this.pageScrollLock;
      if (!lock) return;
      const root = document.documentElement;
      const body = document.body;
      root.classList.remove('vl-collection-compare-open');
      body.classList.remove('vl-collection-compare-open');
      root.style.overflow = lock.rootOverflow;
      body.style.overflow = lock.bodyOverflow;
      body.style.position = lock.bodyPosition;
      body.style.top = lock.bodyTop;
      body.style.left = lock.bodyLeft;
      body.style.right = lock.bodyRight;
      body.style.width = lock.bodyWidth;
      window.scrollTo({ top: lock.scrollY, behavior: 'auto' });
      this.pageScrollLock = null;
    }

    announce(message) {
      const live = this.querySelector('[data-vl-collection-compare-live]');
      if (live) live.textContent = message;
    }

    escape(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  customElements.define(TAG_NAME, VLCollectionCompare);
})();
